import React, { useEffect, useState, useRef } from "react";
import Lucide from "../../base-components/Lucide";
import { useAppDispatch, useAppSelector } from "src/stores";
import {
  selectReminderToasts,
  removeReminderToast,
  addReminderToast,
} from "src/stores/reminderToasts/slice";

const ReminderToast: React.FC = () => {
  const toasts = useAppSelector(selectReminderToasts);
  const dispatch = useAppDispatch();

  // persisted options for the ReminderToast display
  const [options, setOptions] = useState(() => {
    try {
      const raw = localStorage.getItem("reminderToastOptions");
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          // Enforce 1 minute auto-dismiss per latest requirement
          parsed.autoDismiss = true;
          parsed.dismissMs = 60000; // 60s
          localStorage.setItem("reminderToastOptions", JSON.stringify(parsed));
          return parsed;
        } catch {}
      }
      // Default with forced 1 minute auto-dismiss
      const defaults = {
        maxStack: 4,
        hoverExpand: true,
        autoDismiss: true,
        dismissMs: 60000,
      };
      localStorage.setItem("reminderToastOptions", JSON.stringify(defaults));
      return defaults;
    } catch (err) {
      return {
        maxStack: 4,
        hoverExpand: true,
        autoDismiss: true,
        dismissMs: 60000,
      };
    }
  });

  // Removed expanded "more" functionality
  const timersRef = useRef<Record<string, number>>({});

  const saveOptions = (
    newOpts: Partial<{
      maxStack: number;
      hoverExpand: boolean;
      autoDismiss: boolean;
      dismissMs: number;
    }>
  ) => {
    const merged = { ...options, ...newOpts } as any;
    setOptions(merged);
    try {
      localStorage.setItem("reminderToastOptions", JSON.stringify(merged));
    } catch (err) {
      // ignore
    }
  };

  // Auto-dismiss management: creates per-toast timers when enabled
  useEffect(() => {
    // clear previous timers
    Object.values(timersRef.current).forEach((t) => {
      try {
        window.clearTimeout(t as any);
      } catch (e) {}
    });
    timersRef.current = {};

    if (options.autoDismiss && options.dismissMs && options.dismissMs > 0) {
      toasts.forEach((t) => {
        const id = window.setTimeout(() => {
          dispatch(removeReminderToast(t.id));
        }, options.dismissMs);
        timersRef.current[t.id] = id;
      });
    }

    return () => {
      Object.values(timersRef.current).forEach((t) => {
        try {
          window.clearTimeout(t as any);
        } catch (e) {}
      });
      timersRef.current = {};
    };
  }, [toasts, options, dispatch]);

  useEffect(() => {
    function handler(e: Event) {
      const ce = e as CustomEvent;

      const payload = ce.detail || {};
      try {
        const genId = () => {
          try {
            // @ts-ignore crypto may exist in browsers
            if (typeof crypto !== "undefined" && (crypto as any).randomUUID) {
              return (crypto as any).randomUUID();
            }
          } catch (e) {}
          return `reminder-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 9)}`;
        };
        const id = payload.id || genId();
        const helper = (window as any).__addReminderToastFromEvent;
        if (typeof helper === "function") {
          const action = helper(id, payload);
          if (action) dispatch(action);
        } else {
          // Fallback: dispatch directly
          dispatch(
            // @ts-ignore
            addReminderToast({ id, ...payload, createdAt: Date.now() } as any)
          );
        }
      } catch (err) {
        // ignore
      }
    }

    window.addEventListener("show-reminder-toast", handler as EventListener);
    return () =>
      window.removeEventListener(
        "show-reminder-toast",
        handler as EventListener
      );
  }, [dispatch]);

  // Remove toasts when a reminder is updated elsewhere (complete/snooze)
  useEffect(() => {
    function onReminderUpdated(e: Event) {
      try {
        const ce = e as CustomEvent;
        const id = ce.detail?.id;
        if (id) {
          // ensure string payload for reducer
          dispatch(removeReminderToast(String(id)));
        }
      } catch (err) {
        // ignore
      }
    }

    window.addEventListener(
      "reminder-updated",
      onReminderUpdated as EventListener
    );
    return () =>
      window.removeEventListener(
        "reminder-updated",
        onReminderUpdated as EventListener
      );
  }, [dispatch]);

  // Also remove toast as soon as a Complete/Snooze modal opens anywhere
  useEffect(() => {
    const onOpenComplete = (e: Event) => {
      try {
        const ce = e as CustomEvent;
        const rid = ce?.detail?.item?.id || ce?.detail?.id;
        if (rid) dispatch(removeReminderToast(String(rid)));
      } catch {}
    };
    const onOpenSnooze = (e: Event) => {
      try {
        const ce = e as CustomEvent;
        const rid = ce?.detail?.item?.id || ce?.detail?.id;
        if (rid) dispatch(removeReminderToast(String(rid)));
      } catch {}
    };

    window.addEventListener(
      "open-complete-modal",
      onOpenComplete as EventListener
    );
    window.addEventListener("open-snooze-modal", onOpenSnooze as EventListener);
    return () => {
      window.removeEventListener(
        "open-complete-modal",
        onOpenComplete as EventListener
      );
      window.removeEventListener(
        "open-snooze-modal",
        onOpenSnooze as EventListener
      );
    };
  }, [dispatch]);

  if (!toasts || toasts.length === 0) return null;

  const MAX_STACK = options.maxStack || 4;

  const fingerprint = (t: (typeof toasts)[number]) => {
    try {
      const notifId = t?.meta?.raw?.notification?.id;
      if (notifId) return `notif:${notifId}`;

      const remId = t?.meta?.reminderId || t?.meta?.reminder?.id;
      if (remId) return `rem:${remId}`;

      return `txt:${(t.title || "").trim()}::${(t.description || "").trim()}`;
    } catch (e) {
      return `txt:${(t.title || "").trim()}::${(t.description || "").trim()}`;
    }
  };

  // dedupe by fingerprint while preserving order (newest first)
  const seen = new Set<string>();
  const deduped = [] as typeof toasts;
  for (const t of toasts) {
    const key = fingerprint(t);
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(t);
    }
  }
  const total = deduped.length;

  const stack = deduped.slice(0, Math.min(MAX_STACK, deduped.length));

  const getReminderId = (t: (typeof toasts)[number]) => {
    return (
      t?.meta?.raw?.notification?.additionalData?.reminder_id ||
      t?.meta?.reminderId ||
      t?.meta?.reminder?.id ||
      undefined
    );
  };

  const IsReminder = (t: (typeof toasts)[number]) => {
    const type = t?.meta?.raw?.notification?.additionalData?.notification_type;

    return type === "reminder" || type === "due" || type === "advanc";
  };
  return (
    <div className="fixed right-5 top-24 z-50 w-[520px] transform transition-all duration-300">
      {/* settings gear */}
      <div className="absolute -top-10 right-0">
        <div className="flex items-center gap-2">
          <button
            title="Reminder Toast Settings"
            onClick={() => {
              const panel = document.getElementById("reminder-toast-settings");
              if (panel) panel.classList.toggle("hidden");
            }}
            className="bg-white/90 p-2 rounded-full shadow border"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-slate-700"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3"
              />
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>

          <div
            id="reminder-toast-settings"
            className="hidden bg-white p-3 rounded-lg shadow border text-sm w-64"
          >
            <div className="mb-2 font-semibold">Reminder Toast Settings</div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-slate-700">Max stacked</label>
              <input
                type="number"
                min={1}
                max={10}
                value={MAX_STACK}
                onChange={(e) =>
                  saveOptions({
                    maxStack: Math.max(1, Number(e.target.value) || 1),
                  })
                }
                className="w-16 border rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-slate-700">Hover expand</label>
              <input
                type="checkbox"
                checked={!!options.hoverExpand}
                onChange={(e) => saveOptions({ hoverExpand: e.target.checked })}
              />
            </div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-slate-700">Auto dismiss</label>
              <input
                type="checkbox"
                checked={!!options.autoDismiss}
                onChange={(e) => saveOptions({ autoDismiss: e.target.checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-700">Timeout (ms)</label>
              <input
                type="number"
                min={0}
                value={options.dismissMs || 0}
                onChange={(e) =>
                  saveOptions({
                    dismissMs: Math.max(0, Number(e.target.value) || 0),
                  })
                }
                className="w-24 border rounded px-2 py-1 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {
        <div className="space-y-4">
          {stack.map((t) => {
            // Only render the reminder alert UI when it matches our types
            if (!IsReminder(t)) return null;

            return (
              <div
                key={t.id}
                className="bg-white rounded-2xl border-2 border-red-600 shadow-xl overflow-hidden"
              >
                <div className="relative p-5">
                  {/* CLOSE BUTTON */}
                  <button
                    aria-label={`close-${t.id}`}
                    onClick={() => {
                      const rid = getReminderId(t);
                      dispatch(removeReminderToast(rid || t.id));
                    }}
                    className="absolute top-2 right-2 bg-black text-white w-8 h-8 rounded-full flex items-center justify-center shadow-md z-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 8.586L15.293 3.293a1 1 0 111.414 1.414L11.414 10l5.293 5.293a1 1 0 01-1.414 1.414L10 11.414l-5.293 5.293a1 1 0 01-1.414-1.414L8.586 10 3.293 4.707a1 1 0 011.414-1.414L10 8.586z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {/* REMINDER HEADER */}
                  <div className="text-red-600 font-bold text-xl mb-1">
                    Reminder Alert
                  </div>

                  {/* TITLE */}
                  <div className="text-slate-800 font-semibold text-lg mb-1">
                    {t.title}
                  </div>

                  {/* DESCRIPTION */}
                  {t.description && (
                    <div className="text-slate-600 text-sm mb-3">
                      {t.description}
                    </div>
                  )}

                  {/* ACTION BUTTONS */}
                  <div className="flex items-center gap-3">
                    {t.title !== "Reminder Snoozed" && (
                      <>
                        {/* COMPLETE BUTTON */}
                        <button
                          onClick={() => {
                            const reminderId =
                              t.meta?.raw?.notification?.additionalData
                                ?.reminder_id;

                            try {
                              const item = {
                                id: reminderId,
                                title: t.title,
                                description: t.description,
                                reminder: t.meta?.reminder || null,
                                assignments:
                                  t.meta?.reminder?.assignments || [],
                                query: t.meta?.reminder?.id || undefined,
                              };

                              window.dispatchEvent(
                                new CustomEvent("open-complete-modal", {
                                  detail: { item },
                                })
                              );
                            } catch (err) {
                              console.error(
                                "Failed to dispatch open-complete-modal",
                                err
                              );
                            }

                            if (t.meta?.onComplete) {
                              try {
                                t.meta.onComplete();
                              } catch (err) {}
                            }

                            dispatch(removeReminderToast(reminderId));
                          }}
                          className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-full text-sm shadow"
                        >
                          <span className="inline-flex items-center justify-center w-5 h-5 bg-white rounded-full">
                            <Lucide
                              icon="CheckCircle"
                              className="w-4 h-4 text-red-600"
                            />
                          </span>
                          <span>Complete</span>
                        </button>

                        {/* SNOOZE BUTTON */}
                        {/* <button
                          onClick={() => {
                            const reminderId =
                              t.meta?.raw?.notification?.additionalData
                                ?.reminder_id;

                            try {
                              const item = {
                                id: reminderId,
                                title: t.title,
                                description: t.description,
                                reminder: t.meta?.reminder || null,
                                assignments:
                                  t.meta?.reminder?.assignments || [],
                                query: t.meta?.reminder?.id || undefined,
                              };

                              window.dispatchEvent(
                                new CustomEvent("open-snooze-modal", {
                                  detail: { item },
                                })
                              );
                            } catch (err) {
                              console.error(
                                "Failed to dispatch open-snooze-modal",
                                err
                              );
                            }

                            if (t.meta?.onSnooze) {
                              try {
                                t.meta.onSnooze();
                              } catch (err) {}
                            }

                            const rid = getReminderId(t);
                            dispatch(removeReminderToast(rid || t.id));
                          }}
                          className="flex items-center gap-2 bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm border"
                        >
                          <span className="inline-flex items-center justify-center w-5 h-5 bg-gray-200 rounded-full">
                            <Lucide
                              icon="Clock4"
                              className="w-4 h-4 text-red-600"
                            />
                          </span>
                          <span>Snooze</span>
                        </button> */}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
};

export default ReminderToast;
